import CheckIcon from '@mui/icons-material/Check';
import DownloadIcon from '@mui/icons-material/FileDownload';
import UploadIcon from '@mui/icons-material/FileUpload';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '../../apiForm/panel/Paper';
import { downloadFile, readFile } from './downloadUtils';
import CategoriesIcon from '../../icons/category.svg?react';
import UploadDialog from './FileUpload/UploadDialog';
import useExportApi from './useExportApi';

export default function ProductMenuExport() {
  const { t } = useTranslation();
  const api = useExportApi();
  const { projectId } = useParams();

  const [downloading, setDownloading] = useState(false);
  const [downloadedCategoriesCount, setDownloadedCategoriesCount] = useState<
    number | undefined
  >();
  const [downloadedItemsCount, setDownloadedItemsCount] = useState<
    number | undefined
  >();

  const [uploading, setUploading] = useState(false);
  const [uploadingDialog, setUploadingDialog] = useState(false);
  const [uploadedCategoriesCount, setUploadedCategoriesCount] = useState<
    number | undefined
  >();
  const [uploadedItemsCount, setUploadedItemsCount] = useState<
    number | undefined
  >();

  const handleProductsDownload = useCallback(async () => {
    setDownloading(true);
    const data = await api.getProductMenus(null);
    if (!data) {
      setDownloadedCategoriesCount(0);
      setDownloadedItemsCount(0);
      setDownloading(false);
      return;
    }
    const jsonData = JSON.parse(data);
    setDownloadedCategoriesCount(
      jsonData?.categories ? jsonData.categories.length : 0,
    );
    setDownloadedItemsCount(
      jsonData?.singleItems ? jsonData.singleItems.length : 0,
    );
    downloadFile(
      data,
      `${projectId}-product-menu-export.json`,
      'application/json',
    );
    setDownloading(false);
  }, [api, projectId]);

  const handleProductsUpload = async (file: File) => {
    setUploadingDialog(false);
    setUploading(true);
    const stringData = await readFile(file);
    if (!stringData) return;
    const data = JSON.parse(stringData);
    if (!data) {
      setUploading(false);
      setUploadedCategoriesCount(0);
      setUploadedItemsCount(0);
    }
    setUploadedCategoriesCount(data?.categories ? data.categories.length : 0);
    setUploadedItemsCount(data?.singleItems ? data.singleItems.length : 0);
    api.uploadProductMenus({ ...data, projectID: projectId });
    setUploading(false);
  };

  return (
    <>
      <UploadDialog
        open={uploadingDialog}
        confirmationTitle={t('export.productMenus.confirmation.title')}
        confirmationBody={t('export.productMenus.confirmation.body')}
        onUpload={handleProductsUpload}
        onClose={() => {
          setUploadingDialog(false);
        }}
        metadataParser={async (file: File) => {
          const stringData = await readFile(file);
          const data = JSON.parse(stringData);
          return {
            'Product Menus': String(
              data?.categories ? data.categories.length : 0,
            ),
            'Quick Access Items': String(
              data?.singleItems ? data.singleItems.length : 0,
            ),
          };
        }}
      />

      <Paper
        legend={
          <Stack direction="row" gap={1} alignItems="center">
            <CategoriesIcon style={{ fill: 'white' }} />
            <Typography variant="body2">
              {t('export.productMenus.panelHeadline')}
            </Typography>
          </Stack>
        }
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-evenly"
        >
          <Stack direction="column" alignItems="flex-start" gap={2}>
            <Button
              onClick={handleProductsDownload}
              startIcon={<DownloadIcon />}
              variant="contained"
              loading={downloading}
              loadingPosition="start"
            >
              {t('export.productMenus.downloadButton')}
            </Button>
            <Box sx={{ minHeight: '24px' }}>
              {(downloadedCategoriesCount !== undefined ||
                downloadedItemsCount !== undefined) && (
                <Stack direction="row" gap={1} alignItems="center">
                  <CheckIcon color="success" fontSize="small" />
                  <Typography variant="caption">
                    {t('export.productMenus.downloadedHint', {
                      menuCount: downloadedCategoriesCount,
                      itemCount: downloadedItemsCount,
                    })}
                  </Typography>
                </Stack>
              )}
            </Box>
          </Stack>

          <Stack direction="column" alignItems="flex-start" gap={2}>
            <Button
              onClick={() => {
                setUploadingDialog(true);
              }}
              startIcon={<UploadIcon />}
              variant="contained"
              loading={uploading}
              loadingPosition="start"
            >
              {t('export.productMenus.uploadButton')}
            </Button>
            <Box sx={{ minHeight: '24px' }}>
              {(uploadedCategoriesCount !== undefined ||
                uploadedItemsCount !== undefined) && (
                <Stack direction="row" gap={1} alignItems="center">
                  <CheckIcon color="success" fontSize="small" />
                  <Typography variant="caption">
                    {t('export.productMenus.uploadedHint', {
                      menuCount: uploadedCategoriesCount,
                      itemCount: uploadedItemsCount,
                    })}
                  </Typography>
                </Stack>
              )}
            </Box>
          </Stack>
        </Stack>

        <Alert severity="info" sx={{ marginTop: '16px' }}>
          <Typography variant="body2">
            {t('export.productMenus.noShopsHint')}
          </Typography>
        </Alert>
      </Paper>
    </>
  );
}
